import Swal from 'sweetalert2'
import type { Conversation, WaitInfo, DurationInfo } from '@/types/dashboard'

export const FLUXO_MAP: Record<number, string> = {
  1: 'Geral',
  2: 'Empresa Cliente',
  3: 'Empresa Não Cliente',
  4: 'Jovem Aprendiz',
  5: 'Estagiário',
  6: 'Candidato',
}

export function fluxoLabel(id: number | null | undefined): string {
  if (!id) return '—'
  return FLUXO_MAP[id] || `Fluxo ${id}`
}

/** Categorias de especialidade por agente/time (tabela agent_categories) — mesmas 4 usadas em AgentesView.vue.
 * A chave de R&S é literalmente "r&s" (com "&") — não "rs" — porque é o valor que já está
 * gravado no banco (categories é um JSON array de strings); ver AgentesView.vue. */
export const AGENT_CATEGORY_OPTIONS: { key: 'aprendizagem' | 'comercial' | 'financeiro' | 'r&s'; label: string; color: string }[] = [
  { key: 'aprendizagem', label: 'Aprendizagem', color: 'var(--green)' },
  { key: 'comercial',    label: 'Comercial',    color: 'var(--blue)' },
  { key: 'financeiro',   label: 'Financeiro',   color: 'var(--orange)' },
  { key: 'r&s',          label: 'R&S',          color: '#7C3AED' },
]

/** Regra fixa negócio: cada categoria sempre atende um fluxo específico —
 * Aprendizagem → Jovem Aprendiz, Comercial → Empresa Não Cliente, Financeiro
 * → Empresa Cliente, R&S → Candidato. Usado por withDefaultFluxos() pra marcar
 * esse fluxo já na tela mesmo antes de qualquer gravação no banco
 * (agent_categories/team_fallback_agents). */
export const CATEGORY_DEFAULT_FLUXO: Record<string, number> = {
  aprendizagem: 4, // Jovem Aprendiz
  comercial:    3, // Empresa Não Cliente
  financeiro:   2, // Empresa Cliente
  'r&s':        6, // Candidato
}

/** Une os fluxos já salvos com os implícitos pelas categorias ativas (ver
 * CATEGORY_DEFAULT_FLUXO) — usado tanto pra exibir o toggle já marcado
 * quanto pra persistir esse fluxo junto na próxima gravação. */
export function withDefaultFluxos(categories: string[], fluxoIds: number[]): number[] {
  const implied = categories
    .map(c => CATEGORY_DEFAULT_FLUXO[c])
    .filter((id): id is number => id !== undefined)
  return Array.from(new Set([...fluxoIds, ...implied]))
}

const FLUXO_TO_CATEGORY: Record<number, 'aprendizagem' | 'comercial' | 'financeiro' | 'r&s'> = Object.fromEntries(
  Object.entries(CATEGORY_DEFAULT_FLUXO).map(([cat, fluxoId]) => [fluxoId, cat])
) as Record<number, 'aprendizagem' | 'comercial' | 'financeiro' | 'r&s'>

/** Categoria de negócio (Aprendizagem/Comercial/Financeiro/R&S) implícita pelo fluxo da conversa — ver CATEGORY_DEFAULT_FLUXO. null quando o fluxo não corresponde a nenhuma das 4 (ex.: Geral, Estagiário). */
export function businessCategoryForFluxo(fluxoId: number | null | undefined): 'aprendizagem' | 'comercial' | 'financeiro' | 'r&s' | null {
  if (fluxoId == null) return null
  return FLUXO_TO_CATEGORY[fluxoId] ?? null
}

/** Documento capturado pelo bot durante o fluxo, pra exibir no cabeçalho da conversa — CNPJ ou
 * CPF, independente do fluxo: mostra assim que o atributo vier preenchido, não só nos fluxos
 * "esperados" (2/Empresa Cliente, 4/Jovem Aprendiz), já que o bot pode capturar o documento antes
 * de a conversa mudar de fluxo. CNPJ tem prioridade se ambos vierem preenchidos. null quando
 * nenhum dos dois veio. Valor cru, sem máscara — do jeito que a API devolve. */
export function conversationDocument(conv: { cpf?: string | null; cnpj?: string | null }): { label: string; value: string } | null {
  if (conv.cnpj) return { label: 'CNPJ', value: conv.cnpj }
  if (conv.cpf)  return { label: 'CPF', value: conv.cpf }
  return null
}

/** true quando a conversa ainda está no fluxo Geral (fluxo_id 1) do robô — mesmo fallback usado ao normalizar Conversation. */
export function isFluxoGeral(fluxoId: number | null | undefined): boolean {
  return (fluxoId ?? 1) === 1
}

/** Confirmação obrigatória antes de atribuir agente manualmente enquanto a conversa ainda está no fluxo Geral do robô. */
export async function confirmAssignDuringFluxoGeral(): Promise<boolean> {
  const result = await Swal.fire({
    title: 'Atribuir agente durante o fluxo do robô?',
    html: 'Você está atribuindo um agente em meio ao fluxo do robô. A conversa será interrompida com o robô para dar início ao atendimento humano.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#BC0C24',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sim, atribuir',
    cancelButtonText: 'Não, continuar o fluxo',
    reverseButtons: true,
  })
  return result.isConfirmed
}

/** Confirmação obrigatória antes de resolver uma conversa — usada por ConvDrawer/FloatChatWindow/MobileView, os 3 lugares onde o agente clica em "Resolver". */
export async function confirmResolveConversation(): Promise<boolean> {
  const result = await Swal.fire({
    title: 'Resolver esta conversa?',
    html: 'Esta conversa será resolvida e não poderá ser aberta novamente. Tem certeza?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#BC0C24',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sim, resolver',
    cancelButtonText: 'Cancelar',
    reverseButtons: true,
  })
  return result.isConfirmed
}

const COLORS = ['#BC0C24','#FF6600','#1F8C5A','#0369A1','#7C3AED','#B45309','#C026D3','#0F766E','#73010F','#1D4ED8']

export function avatarColor(str: string): string {
  let h = 0
  for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return COLORS[Math.abs(h) % COLORS.length]
}

export function initials(name: string): string {
  return (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

export function fmtChannel(ch: string): string {
  const map: Record<string, string> = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::Api': 'API',
    'Channel::Email': 'Email',
    'Channel::WebWidget': 'Widget',
    'Channel::Telegram': 'Telegram',
  }
  return map[ch] || (ch || '').replace('Channel::', '') || '—'
}

export function fmtWait(ts: number): WaitInfo {
  if (!ts || ts === 0) return { label: '—', cls: 'none' }
  const mins = Math.floor((Date.now() / 1000 - ts) / 60)
  if (mins < 0) return { label: '—', cls: 'none' }
  if (mins < 5)   return { label: mins + 'min',                                    cls: 'cold' }
  if (mins < 30)  return { label: mins + 'min',                                    cls: 'warm' }
  if (mins < 120) return { label: Math.floor(mins / 60) + 'h' + (mins % 60) + 'min', cls: 'hot' }
  return { label: Math.floor(mins / 60) + 'h', cls: 'hot' }
}

export function fmtEpochDateTime(ts: number): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** yyyyMMdd (data de criação) + id da conversa com zero-fill de 7 dígitos — mesmo
 * formato de protocol() em utils/avaliacoesFormat.ts (lá é por data da avaliação;
 * aqui, por data de criação da conversa, já que não há evento de avaliação). */
export function conversationProtocol(c: Conversation): string {
  const d = new Date((c.created_at || 0) * 1000)
  const y   = d.getFullYear()
  const m   = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}${m}${day}${String(c.id).padStart(7, '0')}`
}

export function fmtDuration(secs: number): string | null {
  if (!secs || secs <= 0) return null
  const m = Math.floor(secs / 60)
  const h = Math.floor(m / 60)
  const d = Math.floor(h / 24)
  if (d > 0) return `${d}d ${h % 24}h`
  if (h > 0) return `${h}h ${m % 60 > 0 ? (m % 60) + 'min' : ''}`.trim()
  if (m > 0) return `${m}min`
  return '<1min'
}

export function convDuration(c: Conversation): DurationInfo {
  if (c.status === 'resolved' && c.resolved_at && c.created_at) {
    const secs = c.resolved_at - c.created_at
    return { label: fmtDuration(secs) || '—', type: 'resolved', secs }
  }
  if (c.created_at) {
    const secs = Math.floor(Date.now() / 1000) - c.created_at
    const label = fmtDuration(secs) || '—'
    const type  = secs > 86400 ? 'long' : (c.status === 'pending' ? 'pending' : 'open')
    return { label, type, secs }
  }
  return { label: '—', type: 'none', secs: 0 }
}

export function countUp(el: HTMLElement, to: number): void {
  const dur = 650, start = performance.now()
  const from = parseInt(el.textContent || '0') || 0
  const step = (now: number) => {
    const t = Math.min((now - start) / dur, 1)
    const e = 1 - Math.pow(1 - t, 3)
    el.textContent = String(Math.round(from + (to - from) * e))
    if (t < 1) requestAnimationFrame(step)
    else el.textContent = String(to)
  }
  requestAnimationFrame(step)
}
